import { useEffect, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { LayoutList } from "lucide-react";
import { Callout, EmptyState, LinkButton, useColumnVisibility, useNarrow, errorText } from "@amber/ui";
import type { ItemStatus, ItemsFilter, ItemsResponse, StatusGroup } from "../../contracts/index";
import { itemsQuery, sourceOptionsQuery } from "../queries";
import type { BacklogSearch } from "../search";
import { AddItemPanel } from "./AddItem";
import { BacklogSidebar } from "./BacklogSidebar";
import { BacklogStrip } from "./BacklogStrip";
import { BacklogTable } from "./BacklogTable";
import { BacklogToolbar, backlogChips } from "./BacklogToolbar";
import { BACKLOG_VISIBILITY } from "./columns";
import { ItemDrawer } from "./ItemDrawer";
import { BulkBar, JobBars, RecoveryDialog } from "./Recovery";
import { useBacklogSelection, type BacklogSelection } from "./useBacklogSelection";

export interface BacklogViewProps {
  search: BacklogSearch;
  itemId: string | null;
  adding: string | null;
  onSearch: (next: BacklogSearch) => void;
  onOpenItem: (id: string | null) => void;
  onAdd: (url: string | null) => void;
}

function backlogFilter({ sort, group, ...filter }: BacklogSearch): ItemsFilter {
  return filter;
}

// The status facet counts over the other filters, so it gives the failed items among all matching.
function failedMatching(search: BacklogSearch, data: ItemsResponse | undefined): number {
  const statuses = search.status?.split(",") ?? [];
  if (statuses.length > 0 && !statuses.includes("failed")) return 0;
  return data?.facets.status.find((f) => f.key === "failed")?.count ?? 0;
}

export function BacklogView({ search, itemId, adding, onSearch, onOpenItem, onAdd }: BacklogViewProps) {
  const [limit, setLimit] = useState(100);
  const query = useQuery(itemsQuery({ ...search, ...(limit === 100 ? {} : { limit }) }));
  const sourceOptions = useQuery({ ...sourceOptionsQuery, enabled: search.source !== undefined });
  const [text, setText] = useState(search.q ?? "");
  const [filtersOpen, setFiltersOpen] = useState(false);
  const columns = useColumnVisibility(BACKLOG_VISIBILITY);
  const [collapsed, setCollapsed] = useState<Set<StatusGroup>>(() => new Set(["excluded"]));
  const narrow = useNarrow();
  const [bulkOpen, setBulkOpen] = useState(false);
  const data = query.data;
  const rows = data?.rows ?? [];
  const filter = backlogFilter(search);
  const matching = data?.total ?? 0;
  const selection = useBacklogSelection(rows, JSON.stringify(filter));
  const failed = selection.allMatching ? failedMatching(search, data) : selection.failed;
  const grouped = search.group !== "none";
  const set = (patch: Partial<BacklogSearch>) => {
    onSearch({ ...search, ...patch });
  };

  useEffect(() => {
    const timer = window.setTimeout(() => {
      const q = text.trim() || undefined;
      if (q !== search.q) onSearch({ ...search, q });
    }, 300);
    return () => {
      window.clearTimeout(timer);
    };
  }, [text, search, onSearch]);

  const selectedRow = data?.rows.find((r) => r.id === itemId);
  useEffect(() => {
    if (selectedRow?.status.status === "excluded")
      setCollapsed((c) => (c.has("excluded") ? new Set([...c].filter((g) => g !== "excluded")) : c));
  }, [selectedRow?.status.status]);

  const chips = backlogChips(search, data, sourceOptions.data);
  const reset = () => {
    setText("");
    onSearch({ sort: search.sort, group: search.group });
  };

  return (
    <div className="relative flex min-h-0 flex-1">
      <BacklogSidebar open={filtersOpen} data={data} search={search} set={set} />
      <section aria-labelledby="view-title" className="flex min-h-0 min-w-0 flex-1 flex-col">
        {data ? (
          <BacklogStrip data={data} search={search} set={set} />
        ) : (
          <h1 id="view-title" className="m-0 px-4 pt-3.5 text-2xl font-semibold">
            Backlog
          </h1>
        )}
        <BacklogToolbar
          data={data}
          chips={chips}
          text={text}
          onText={setText}
          filtersOpen={filtersOpen}
          onFilters={() => {
            setFiltersOpen(!filtersOpen);
          }}
          narrow={narrow}
          grouped={grouped}
          showsOthers={search.authors === "all"}
          columns={columns.toggles}
          onResetColumns={columns.reset}
          set={set}
          onAdd={onAdd}
          onReset={reset}
        />
        <BulkBar
          selection={selection}
          rows={rows}
          matching={matching}
          failed={failed}
          canRecover={data?.actions.recover.enabled === false ? data.actions.recover.reason : null}
          onOpen={() => {
            setBulkOpen(true);
          }}
        />
        <JobBars />
        {query.isError ? (
          <Callout tone="attention" role="alert" className="mx-4 mb-2">
            Couldn&apos;t load the backlog. {errorText(query.error)}{" "}
            <LinkButton
              onClick={() => {
                void query.refetch();
              }}
            >
              Retry
            </LinkButton>
          </Callout>
        ) : null}
        {data && data.all === 0 && data.other_accounts === 0 ? (
          <EmptyState icon={<LayoutList />} title="No items yet">
            Items appear here when monitoring discovers them or someone adds one by link.
          </EmptyState>
        ) : (
          <BacklogTable
            data={data}
            isPending={query.isPending}
            grouped={grouped}
            hidden={columns.hidden}
            widthsReset={columns.widthsReset}
            narrow={narrow}
            collapsed={collapsed}
            onToggleGroup={(key, isCollapsed) => {
              const next = new Set(collapsed);
              if (isCollapsed) next.delete(key);
              else next.add(key);
              setCollapsed(next);
            }}
            selection={selection}
            itemId={itemId}
            sort={search.sort}
            onSort={(sort) => {
              set({ sort });
            }}
            onReset={reset}
            limit={limit}
            onMore={() => {
              setLimit(200);
            }}
            onOpenItem={onOpenItem}
          />
        )}
      </section>
      {bulkOpen ? (
        <BulkRecovery
          selection={selection}
          filter={filter}
          matching={matching}
          failed={failed}
          onClose={() => {
            setBulkOpen(false);
          }}
        />
      ) : null}
      <ItemSidePanel adding={adding} itemId={itemId} onAdd={onAdd} onOpenItem={onOpenItem} />
    </div>
  );
}

function BulkRecovery({
  selection,
  filter,
  matching,
  failed,
  onClose,
}: {
  selection: BacklogSelection;
  filter: ItemsFilter;
  matching: number;
  failed: number;
  onClose: () => void;
}) {
  const count = selection.allMatching ? matching : selection.picked.size;
  const items = count === 1 ? "item" : "items";
  return (
    <RecoveryDialog
      ids={[...selection.picked]}
      filter={selection.allMatching ? filter : undefined}
      title={
        selection.allMatching
          ? `Retry or reprocess all ${String(count)} matching ${items}`
          : `Retry or reprocess ${String(count)} selected ${items}`
      }
      initialMode={failed > 0 ? "retry" : "reprocess"}
      onClose={onClose}
      onQueued={() => {
        onClose();
        selection.clear();
      }}
    />
  );
}

function ItemSidePanel({
  adding,
  itemId,
  onAdd,
  onOpenItem,
}: Pick<BacklogViewProps, "adding" | "itemId" | "onAdd" | "onOpenItem">) {
  if (adding !== null)
    return (
      <AddItemPanel
        initialUrl={adding}
        onClose={() => {
          onAdd(null);
        }}
        onAdded={(id) => {
          onAdd(null);
          onOpenItem(id);
        }}
      />
    );
  if (!itemId) return null;
  return (
    <ItemDrawer
      id={itemId}
      onClose={() => {
        onOpenItem(null);
      }}
    />
  );
}

export function BacklogStatus() {
  const query = useQuery(itemsQuery({}));
  const data = query.data;
  if (!data) return null;
  const count = (s: ItemStatus) => data.facets.status.find((f) => f.key === s)?.count ?? 0;
  return (
    <span className="max-narrow:hidden">
      <b className="font-strong text-fg">{count("in_progress")}</b> in progress ·{" "}
      <b className="font-strong text-fg">{count("new")}</b> new ·{" "}
      <b className="font-strong text-fg">{count("failed") + count("waiting")}</b> need attention · {data.all} items
    </span>
  );
}
